import { Page } from 'components/Shared/Page'
import { PageHeader } from 'components/Shared/PageHeader'
import { Button, Stack } from '@mui/material'
import { Link } from 'react-router-dom'
import { SearchField } from 'components/SearchField'
import { useDebounce } from '@uidotdev/usehooks'
import { LEConnectUserTable } from './LEConnectUserTable'
import React, { useEffect, useState } from 'react'
import { type LicensedEstablishmentUserPageResult } from 'types/api'
import { type ApiError } from 'openapi-typescript-fetch'
import { useQuery } from '@tanstack/react-query'
import { useTypedApiClient } from 'hooks/useTypedApiClient'
import { minimumSearchLength } from 'utils/util'
import { type GridPaginationModel } from '@mui/x-data-grid'

export const LEConnectUsersPage = () => {
  const [searchText, setSearchText] = useState('')
  const debouncedQuery = useDebounce(searchText, 500)

  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 25,
    page: 0,
  })

  const getLEUsers = useTypedApiClient({
    path: '/admin/licensed-establishments/users',
    method: 'get',
  })

  const getLEUsersSearched = useTypedApiClient({
    path: '/admin/licensed-establishments/users/search',
    method: 'get',
  })

  useEffect(() => {
    if (debouncedQuery.length >= minimumSearchLength) {
      // set back to page 0 when user searches to make sure results show
      setPaginationModel({ page: 0, pageSize: paginationModel.pageSize })
    }
  }, [debouncedQuery])

  const leUsersQuery = useQuery<LicensedEstablishmentUserPageResult, ApiError>({
    queryKey: ['users', debouncedQuery, paginationModel],
    queryFn: async () => {
      if (debouncedQuery.length >= minimumSearchLength) {
        return (
          await getLEUsersSearched({
            CurrentPage: paginationModel.page + 1,
            PageSize: paginationModel.pageSize,
            searchKey: debouncedQuery,
          })
        ).data
      } else {
        return (
          await getLEUsers({
            CurrentPage: paginationModel.page + 1,
            PageSize: paginationModel.pageSize,
          })
        ).data
      }
    },
  })

  const onPaginationModelChange = (model: GridPaginationModel) => {
    setPaginationModel(model)
  }

  return (
    <Page
      header={
        <PageHeader title="J&J Connect Users" eyebrow="CLIENT MANAGEMENT" />
      }
    >
      <Stack
        direction={{ md: 'row', sm: 'column', xs: 'column' }}
        gap={{ md: 0, sm: 2, xs: 2 }}
        justifyContent={'space-between'}
      >
        <SearchField
          placeholder="Search J&J Connect Users"
          onChange={(e) => setSearchText(e.target.value)}
          fullWidth
        />
        <Link to="/ClientManagement/LEConnectUsers/New">
          <Button variant="contained">Create New J&J Connect User</Button>
        </Link>
      </Stack>
      <LEConnectUserTable
        isError={leUsersQuery.error !== null}
        isPending={
          leUsersQuery.isFetching ||
          leUsersQuery.isPending ||
          leUsersQuery.isLoading
        }
        data={leUsersQuery.data?.results ?? []}
        totalCount={leUsersQuery.data?.rowCount ?? 0}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
      />
    </Page>
  )
}
